/**
 * Event Store: Firestore-backed calendar events with multi-provider sync support.
 * Events are namespaced by dashboardId (for shared dashboards) and userId.
 * Data model supports Google, Microsoft, Apple (CalDAV), Zoho sync sources.
 */

import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

const isPermissionError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    message.includes('Missing or insufficient permissions') ||
    message.includes('permission-denied')
  );
};

let eventRemoteReadable = true;
let eventRemoteWarned = false;

const markEventRemoteUnavailable = (): void => {
  eventRemoteReadable = false;
  if (!eventRemoteWarned) {
    eventRemoteWarned = true;
    console.warn('[eventStore] Firestore denied, using empty local event state');
  }
};

export type EventSource = 'google' | 'microsoft' | 'apple' | 'zoho' | 'local';
export type EventStatus = 'confirmed' | 'tentative' | 'cancelled';
export type ReminderMethod = 'email' | 'notification';
export type AttendeeResponseStatus = 'needsAction' | 'declined' | 'tentativelyAccepted' | 'accepted';

export interface EventReminder {
  minutesBefore: number;
  method: ReminderMethod;
}

export interface EventAttendee {
  email: string;
  name?: string;
  responseStatus: AttendeeResponseStatus;
}

export interface ExternalMapping {
  eventId: string;
  etag?: string;
  changeKey?: string;
  calendarId: string;
  lastSyncedAt: Date;
}

export interface LunarCache {
  phaseName: string;
  phaseFraction: number;
  illuminationPct?: number;
  lunationNumber?: number;
}

export interface CalendarEvent {
  id?: string;
  userId: string;
  dashboardId?: string; // For shared dashboard scoping
  source: EventSource;
  sourceEventId: string; // Provider's event ID (unique per source)
  calendarId: string; // Provider's calendar ID
  title: string;
  description?: string;
  start: Date;
  end: Date;
  timezone: string; // IANA timezone (e.g., 'America/Los_Angeles') - REQUIRED for sync
  allDay: boolean;
  status: EventStatus; // 'confirmed' | 'tentative' | 'cancelled'
  reminders?: EventReminder[];
  attendees?: EventAttendee[];
  location?: string;
  
  // Sharing fields
  organizerId?: string; // userId of organizer
  visibility?: 'private' | 'shared'; // Default: 'private'
  
  // Recurrence fields (Phase 2)
  rrule?: string; // RFC 5545 RRULE string
  exdates?: Date[]; // Exception dates (dates to exclude from recurrence)
  overrides?: Record<string, Partial<CalendarEvent>>; // Instance overrides keyed by date ISO string
  
  // External sync mappings
  externalMappings?: {
    google?: ExternalMapping;
    microsoft?: ExternalMapping;
    appleLocal?: ExternalMapping;
    androidLocal?: ExternalMapping;
  };
  
  // Lunar phase cache (optional, computed at runtime or cached)
  lunarAtStart?: LunarCache;
  lunarAtCreated?: LunarCache;
  
  lastSyncedAt: Date;
  updatedAt: Date;
  createdAt: Date;
}

export interface SyncToken {
  userId: string;
  provider: EventSource;
  calendarId: string;
  token: string; // OAuth access token or CalDAV token
  refreshToken?: string;
  expiresAt?: Date;
  updatedAt: Date;
}

export interface SyncCalendar {
  userId: string;
  provider: EventSource;
  calendarId: string;
  name: string;
  enabled: boolean;
  isPrimary: boolean;
  color?: string;
}

/**
 * Get events for a date range, namespaced by dashboardId and userId.
 */
export async function getEventsForRange(
  userId: string,
  startDate: Date,
  endDate: Date,
  dashboardId?: string
): Promise<CalendarEvent[]> {
  if (!db || !eventRemoteReadable) {
    return [];
  }
  try {
    let q = query(
      collection(db, 'events'),
      where('userId', '==', userId),
      where('start', '>=', Timestamp.fromDate(startDate)),
      where('start', '<=', Timestamp.fromDate(endDate)),
      orderBy('start', 'asc')
    );

    // If dashboardId is provided, filter by it (shared dashboard scope)
    if (dashboardId) {
      q = query(
        collection(db, 'events'),
        where('userId', '==', userId),
        where('dashboardId', '==', dashboardId),
        where('start', '>=', Timestamp.fromDate(startDate)),
        where('start', '<=', Timestamp.fromDate(endDate)),
        orderBy('start', 'asc')
      );
    }

    const snap = await getDocs(q);
    return snap.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        start: data.start?.toDate?.() ?? new Date(),
        end: data.end?.toDate?.() ?? new Date(),
        lastSyncedAt: data.lastSyncedAt?.toDate?.() ?? new Date(),
        updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
        createdAt: data.createdAt?.toDate?.() ?? new Date(),
      } as CalendarEvent;
    }).filter(event => event.start <= endDate && event.end >= startDate);
  } catch (error) {
    if (isPermissionError(error)) {
      markEventRemoteUnavailable();
      return [];
    }
    console.error('[eventStore] getEventsForRange error:', error);
    return [];
  }
}

/**
 * Subscribe to events for a date range with real-time updates.
 */
export function subscribeEventsForRange(
  userId: string,
  startDate: Date,
  endDate: Date,
  dashboardId: string | undefined,
  onData: (events: CalendarEvent[]) => void,
  onError?: (error: Error) => void
): () => void {
  if (!db || !eventRemoteReadable) {
    onData([]);
    return () => {};
  }
  try {
    let q = query(
      collection(db, 'events'),
      where('userId', '==', userId),
      where('start', '>=', Timestamp.fromDate(startDate)),
      where('start', '<=', Timestamp.fromDate(endDate)),
      orderBy('start', 'asc')
    );

    if (dashboardId) {
      q = query(
        collection(db, 'events'),
        where('userId', '==', userId),
        where('dashboardId', '==', dashboardId),
        where('start', '>=', Timestamp.fromDate(startDate)),
        where('start', '<=', Timestamp.fromDate(endDate)),
        orderBy('start', 'asc')
      );
    }

    const unsubscribe = onSnapshot(
      q,
      snap => {
        const events = snap.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            start: data.start?.toDate?.() ?? new Date(),
            end: data.end?.toDate?.() ?? new Date(),
            lastSyncedAt: data.lastSyncedAt?.toDate?.() ?? new Date(),
            updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
          } as CalendarEvent;
        }).filter(event => event.start <= endDate && event.end >= startDate);
        onData(events);
      },
      error => {
        if (isPermissionError(error)) {
          markEventRemoteUnavailable();
          onData([]);
          return;
        }
        console.error('[eventStore] subscribeEventsForRange error:', error);
        onError?.(error as Error);
      }
    );

    return unsubscribe;
  } catch (error) {
    if (isPermissionError(error)) {
      markEventRemoteUnavailable();
      onData([]);
      return () => {};
    }
    console.error('[eventStore] subscribeEventsForRange setup error:', error);
    onError?.(error as Error);
    return () => {};
  }
}

/**
 * Subscribe to PUBLIC shared events for a date range — events explicitly marked
 * `visibility: 'shared'`, readable by anyone (including logged-out visitors).
 * No userId filter: this is the public feed. Firestore rules permit reading only
 * docs where visibility == 'shared'.
 */
export function subscribeSharedEventsForRange(
  startDate: Date,
  endDate: Date,
  onData: (events: CalendarEvent[]) => void,
  onError?: (error: Error) => void
): () => void {
  if (!db) {
    onData([]);
    return () => {};
  }
  try {
    const q = query(
      collection(db, 'events'),
      where('visibility', '==', 'shared'),
      where('start', '>=', Timestamp.fromDate(startDate)),
      where('start', '<=', Timestamp.fromDate(endDate)),
      orderBy('start', 'asc')
    );
    const unsubscribe = onSnapshot(
      q,
      snap => {
        const events = snap.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            start: data.start?.toDate?.() ?? new Date(),
            end: data.end?.toDate?.() ?? new Date(),
            lastSyncedAt: data.lastSyncedAt?.toDate?.() ?? new Date(),
            updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
          } as CalendarEvent;
        }).filter(event => event.start <= endDate && event.end >= startDate);
        onData(events);
      },
      error => {
        if (isPermissionError(error)) { onData([]); return; }
        console.error('[eventStore] subscribeSharedEventsForRange error:', error);
        onError?.(error as Error);
      }
    );
    return unsubscribe;
  } catch (error) {
    console.error('[eventStore] subscribeSharedEventsForRange setup error:', error);
    onError?.(error as Error);
    return () => {};
  }
}

/**
 * Create a new event (local or draft).
 */
export async function createEvent(event: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt' | 'lastSyncedAt'>): Promise<string> {
  try {
    const now = new Date();
    const docRef = await addDoc(collection(db, 'events'), {
      ...event,
      start: Timestamp.fromDate(event.start),
      end: Timestamp.fromDate(event.end),
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      lastSyncedAt: Timestamp.fromDate(now),
    });
    return docRef.id;
  } catch (error) {
    console.error('[eventStore] createEvent error:', error);
    throw error;
  }
}

/**
 * Update an existing event.
 */
export async function updateEvent(eventId: string, updates: Partial<CalendarEvent>): Promise<void> {
  try {
    const now = new Date();
    const eventRef = doc(db, 'events', eventId);
    await updateDoc(eventRef, {
      ...updates,
      ...(updates.start && { start: Timestamp.fromDate(updates.start) }),
      ...(updates.end && { end: Timestamp.fromDate(updates.end) }),
      updatedAt: Timestamp.fromDate(now),
    });
  } catch (error) {
    console.error('[eventStore] updateEvent error:', error);
    throw error;
  }
}

/**
 * Delete an event.
 */
export async function deleteEvent(eventId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'events', eventId));
  } catch (error) {
    console.error('[eventStore] deleteEvent error:', error);
    throw error;
  }
}

/**
 * Get events for a specific day.
 */
export async function getEventsForDay(userId: string, date: Date, dashboardId?: string): Promise<CalendarEvent[]> {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);
  return getEventsForRange(userId, dayStart, dayEnd, dashboardId);
}

/**
 * Get sync token for a provider.
 */
export async function getSyncToken(userId: string, provider: EventSource, calendarId: string): Promise<SyncToken | null> {
  try {
    const q = query(
      collection(db, 'syncTokens'),
      where('userId', '==', userId),
      where('provider', '==', provider),
      where('calendarId', '==', calendarId)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const data = snap.docs[0].data();
    return {
      ...data,
      updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
      expiresAt: data.expiresAt?.toDate?.(),
    } as SyncToken;
  } catch (error) {
    console.error('[eventStore] getSyncToken error:', error);
    return null;
  }
}

/**
 * Save or update sync token (usually called by sync Functions).
 */
export async function saveSyncToken(token: SyncToken): Promise<void> {
  try {
    const q = query(
      collection(db, 'syncTokens'),
      where('userId', '==', token.userId),
      where('provider', '==', token.provider),
      where('calendarId', '==', token.calendarId)
    );
    const snap = await getDocs(q);
    if (snap.empty) {
      await addDoc(collection(db, 'syncTokens'), {
        ...token,
        updatedAt: Timestamp.fromDate(token.updatedAt),
        ...(token.expiresAt && { expiresAt: Timestamp.fromDate(token.expiresAt) }),
      });
    } else {
      await updateDoc(snap.docs[0].ref, {
        token: token.token,
        ...(token.refreshToken && { refreshToken: token.refreshToken }),
        ...(token.expiresAt && { expiresAt: Timestamp.fromDate(token.expiresAt) }),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    }
  } catch (error) {
    console.error('[eventStore] saveSyncToken error:', error);
    throw error;
  }
}

/**
 * Get enabled sync calendars for a user.
 */
export async function getEnabledSyncCalendars(userId: string): Promise<SyncCalendar[]> {
  try {
    const q = query(
      collection(db, 'syncCalendars'),
      where('userId', '==', userId),
      where('enabled', '==', true)
    );
    const snap = await getDocs(q);
    return snap.docs.map(doc => doc.data() as SyncCalendar);
  } catch (error) {
    console.error('[eventStore] getEnabledSyncCalendars error:', error);
    return [];
  }
}

/**
 * Save or update sync calendar (user preference on which calendars to sync).
 */
export async function saveSyncCalendar(calendar: SyncCalendar): Promise<void> {
  try {
    const q = query(
      collection(db, 'syncCalendars'),
      where('userId', '==', calendar.userId),
      where('provider', '==', calendar.provider),
      where('calendarId', '==', calendar.calendarId)
    );
    const snap = await getDocs(q);
    if (snap.empty) {
      await addDoc(collection(db, 'syncCalendars'), calendar);
    } else {
      const updateData: any = { ...calendar };
      await updateDoc(snap.docs[0].ref, updateData);
    }
  } catch (error) {
    console.error('[eventStore] saveSyncCalendar error:', error);
    throw error;
  }
}
